import { SYMBOLS } from '@/constants';
import type { FileType } from '@/types/uploadFiles';

import { formatPrice } from '../formatter';
import { formatNumber } from '../numberFormatter';

type TUserNamesKeys = 'firstName' | 'lastName' | 'middleName';
type TUserNamesSnakeCaseKeys = 'first_name' | 'last_name' | 'middle_name';
type TUserNames = Partial<
  Record<TUserNamesKeys | TUserNamesSnakeCaseKeys, string | null>
> | null;

const russianToAscii: { [key: string]: string } = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'yo',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya'
};

/**
 * Склонение слова в зависимости от количества
 * @param words Пример: [файлов, файл, файла, файлов]
 * @param count
 * @returns
 */
export const getRecordWordForm = (
  words: string[],
  count: number,
  formatter?: (count: number) => string
): string => {
  const { isValid, formattedValue, rawValue } = formatNumber(count);

  if (!isValid) {
    return `${formattedValue} ${words[3]}`;
  }

  const absCount = Math.abs(rawValue);
  const formattedCount = formatter ? formatter(rawValue) : formattedValue;

  if (!Number.isInteger(rawValue)) {
    return `${formattedCount} ${words[2]}`;
  }

  const lastDigit = absCount % 10;
  const lastTwoDigits = absCount % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return `${formattedCount} ${words[0]}`;
  }

  if (lastDigit === 1) {
    return `${formattedCount} ${words[1]}`;
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return `${formattedCount} ${words[2]}`;
  }

  return `${formattedCount} ${words[3]}`;
};

export const validateHTMLString = (html: unknown): html is string => {
  // Проверка на null, undefined и не-строки
  if (!html || typeof html !== 'string') {
    return false;
  }

  // Парсим строку HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Рекурсивная функция для проверки текста в узлах
  function hasTextContent(node: Node): boolean {
    // Если это текстовый узел, проверяем, есть ли текст
    if (node.nodeType === Node.TEXT_NODE && node.textContent) {
      return node.textContent?.trim().length > 0;
    }

    // Если это элемент, проверяем его дочерние узлы
    if (node.nodeType === Node.ELEMENT_NODE) {
      for (const child of Array.from(node.childNodes)) {
        if (hasTextContent(child)) {
          return true;
        }
      }
    }

    return false;
  }

  // Проверяем <body> документа
  return hasTextContent(doc.body);
};

/**
 * Форматирует имена пользователя, преобразуя ключи camelCase или snake_case в camelCase,
 * применяя значения по умолчанию, если они предоставлены или если имена отсутствуют.
 *
 * @param userNames - Объект имен пользователя, содержащий firstName, lastName, middleName
 *                    в формате camelCase или snake_case, или null.
 * @param defaultValues - Необязательные значения по умолчанию для firstName, lastName, middleName.
 * @returns Объект с ключами в формате camelCase (firstName, lastName, middleName) и строковыми значениями.
 */
const formatUserNames = (
  userNames: TUserNames | null | undefined,
  defaultValues?: Partial<Record<TUserNamesKeys, string>>
): Record<TUserNamesKeys, string> => {
  const getUpperCasedName = (name: string): string => {
    if (!name) return '';
    return name[0].toUpperCase() + name.slice(1);
  };

  const firstName =
    userNames?.firstName ||
    userNames?.first_name ||
    defaultValues?.firstName ||
    '';
  const lastName =
    userNames?.lastName ||
    userNames?.last_name ||
    defaultValues?.lastName ||
    '';
  const middleName =
    userNames?.middleName ||
    userNames?.middle_name ||
    defaultValues?.middleName ||
    '';

  const upperCaseFirstName = getUpperCasedName(firstName);
  const upperCaseLastName = getUpperCasedName(lastName);
  const upperCaseMiddleName = getUpperCasedName(middleName);

  return {
    firstName: upperCaseFirstName,
    lastName: upperCaseLastName,
    middleName: upperCaseMiddleName
  };
};

/**
 * Формирует полное имя пользователя на основе предоставленных имен, шаблона и конфигурации.
 *
 * @param userNames - Объект имен пользователя, содержащий firstName, lastName, middleName
 *                    в формате camelCase или snake_case, или null.
 * @param config - Объект конфигурации для управления форматированием имени.
 * @param config.hide - Необязательные флаги для скрытия определенных частей имени (firstName, lastName, middleName).
 * @param config.defaultValues - Необязательные значения по умолчанию для частей имени или результата.
 * @param config.pattern - Порядок частей имени (по умолчанию 'last_first_middle').
 * @returns Отформатированная строка полного имени или значение по умолчанию для результата, если имя не сформировано.
 */
export const getUserFullName = (
  userNames: TUserNames | null | undefined,
  config?: {
    hide?: Partial<Record<TUserNamesKeys, boolean>>;
    defaultValues?: Partial<Record<TUserNamesKeys | 'result', string>>;
    pattern?:
      | 'last_first_middle'
      | 'first_middle_last'
      | 'first_last_middle'
      | 'first_last';
  }
): string => {
  const { hide, defaultValues, pattern = 'last_first_middle' } = config || {};
  const formattedUserNames = formatUserNames(userNames, defaultValues);

  let result = '';
  const appendPart = (key: TUserNamesKeys) => {
    const part = formattedUserNames[key];
    if (!hide?.[key] && part) {
      result += (result ? ' ' : '') + part;
    }
  };

  (
    ({
      first_last: () => {
        appendPart('firstName');
        appendPart('lastName');
      },
      first_last_middle: () => {
        appendPart('firstName');
        appendPart('lastName');
        appendPart('middleName');
      },
      first_middle_last: () => {
        appendPart('firstName');
        appendPart('middleName');
        appendPart('lastName');
      },
      last_first_middle: () => {
        appendPart('lastName');
        appendPart('firstName');
        appendPart('middleName');
      }
    }) satisfies Record<typeof pattern, () => void>
  )[pattern]();

  return result || defaultValues?.result || '';
};

/**
 * Формирует инициалы пользователя из объекта имен или строки полного имени.
 *
 * @param userNames - Объект имен пользователя, строка полного имени, null или undefined.
 * @param config - Объект конфигурации для управления форматированием инициалов.
 * @param config.hide - Необязательные флаги для скрытия определенных частей имени (firstName, lastName, middleName).
 * @param config.defaultValues - Необязательные значения по умолчанию для частей имени или результата.
 * @returns Строка, содержащая инициалы пользователя (например, "Иванов И.П.") или значение по умолчанию для результата.
 */
export const getUserInitials = (
  userNames: TUserNames | string | null | undefined,
  config?: {
    hide?: Partial<Record<TUserNamesKeys, boolean>>;
    defaultValues?: Partial<Record<TUserNamesKeys | 'result', string>>;
  }
): string => {
  const { hide, defaultValues } = config || {};
  const isString = typeof userNames === 'string';
  const splitedUserNames = isString
    ? userNames.trim().replace(/\s+/g, ' ').split(' ')
    : [];
  const computedUserNames: TUserNames =
    (isString
      ? {
          firstName: splitedUserNames[1],
          lastName: splitedUserNames[0],
          middleName: splitedUserNames[2]
        }
      : userNames) || {};

  const formattedUserNames = formatUserNames(computedUserNames, defaultValues);

  let initials = '';
  if (!hide?.firstName && formattedUserNames.firstName) {
    initials += `${formattedUserNames.firstName[0]}.`;
  }
  if (!hide?.middleName && formattedUserNames.middleName) {
    initials += `${formattedUserNames.middleName[0]}.`;
  }

  let result = '';
  if (!hide?.lastName && formattedUserNames.lastName) {
    result = formattedUserNames.lastName;
    if (initials) {
      result += ' ' + initials;
    }
  } else if (initials) {
    result = initials;
  }

  return result || defaultValues?.result || '';
};

/**
 * Оборачивает строку указанными кавычками или угловыми кавычками.
 *
 * @param str - Входная строка, которую нужно обернуть.
 * @param type - Тип кавычек для использования. Может быть одним из:
 *   - `'single'`: Оборачивает строку одинарными кавычками (например, 'str').
 *   - `'double'`: Оборачивает строку двойными кавычками (например, "str").
 *   - `'backtick'`: Оборачивает строку обратными кавычками (например, `str`).
 *   - `'guillemet'`: Оборачивает строку угловыми кавычками (например, «str»). Значение по умолчанию.
 *   - `'none'`: Возвращает строку без изменений.
 * @returns Входная строка, обернутая указанными кавычками, или исходная строка, если тип 'none' или недопустим.
 *
 * @example
 * ```typescript
 * surroundWithQuotes("привет"); // Возвращает «привет»
 * surroundWithQuotes("привет", "single"); // Возвращает 'привет'
 * surroundWithQuotes("привет", "double"); // Возвращает "привет"
 * surroundWithQuotes("привет", "backtick"); // Возвращает `привет`
 * surroundWithQuotes("привет", "none"); // Возвращает привет
 * ```
 */
export const surroundWithQuotes = (
  str: string,
  type: 'single' | 'double' | 'backtick' | 'guillemet' | 'none' = 'guillemet'
): string => {
  switch (type) {
    case 'single':
      return `\u0027${str}\u0027`; // 'str'
    case 'double':
      return `\u0022${str}\u0022`; // "str"
    case 'backtick':
      return `\u0060${str}\u0060`; // `str`
    case 'guillemet':
      return `\u00AB${str}\u00BB`; // «str»
    case 'none':
    default:
      return str;
  }
};

/**
 * Проверяет строку Base64 и возвращает объект с информацией о её валидности и типе содержимого.
 * @param base64Str - Входная строка Base64
 * @returns Объект с булевыми ключами:
 * - isValidBase64: Является ли строка валидной Base64
 * - isXML: Содержит ли декодированная строка XML
 */
export function checkBase64(base64Str: string): {
  isValidBase64: boolean;
  isXML: boolean;
} {
  const base64Regex =
    /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
  const isValidBase64 = base64Str.length > 0 && base64Regex.test(base64Str);

  const result = {
    isValidBase64,
    isXML: false
  };

  if (!isValidBase64) {
    return result;
  }

  let decoded: string | null;
  try {
    decoded = Buffer.from(base64Str, 'base64').toString('utf-8');
  } catch {
    return result;
  }

  result.isXML = decoded.trim().startsWith('<?xml');

  return result;
}

const collapseAsciiControlRuns = (value: string): string => {
  let result = '';
  let pendingUnderscore = false;

  for (let i = 0; i < value.length; i++) {
    const ch = value.charAt(i);
    const code = ch.charCodeAt(0);
    if (code <= 31 || code === 127) {
      pendingUnderscore = true;
    } else {
      if (pendingUnderscore) {
        result += '_';
        pendingUnderscore = false;
      }
      result += ch;
    }
  }

  if (pendingUnderscore) result += '_';

  return result;
};

/**
 * Очищает имя файла или папки от недопустимых символов.
 * Разрешены только буквы, цифры, пробел, дефис, подчеркивание и точка.
 * Все остальные символы заменяются на "_".
 *
 * @param name исходное имя файла или папки
 * @param maxLength (необязательно) максимальная длина строки
 * @returns безопасное имя для использования в ZIP, файловой системе и т.д.
 */
export function sanitizeFileName(
  name: string,
  maxLength: number = 255
): string {
  if (!name) return '_';

  const sanitized = collapseAsciiControlRuns(name)
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/[^\p{L}\p{N}\p{M}\p{Zs}\-_.]/gu, '_')
    .trim();

  const cleaned = sanitized.replace(/[. ]+$/g, '');

  return cleaned.slice(0, maxLength) || '_';
}

export const toEnglishAscii = (text: string): string => {
  return text
    .toLowerCase()
    .split('')
    .map((char) => russianToAscii[char] || char)
    .join('')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

export const toTengePrice = (
  amount: string | number | null | undefined
): string => {
  const stringAmount: string = formatPrice(amount ?? '', {
    fallback: SYMBOLS.longDash
  });

  return `${stringAmount} ${SYMBOLS.tenge}`.trim();
};

export const splitBase64 = (
  base64Str: string | null | undefined
): { format: string; base64: string } => {
  if (!base64Str) return { format: '', base64: '' };
  const [format, base64] = base64Str.split(',') as [
    string | undefined,
    string | undefined
  ];
  return { format: format || '', base64: base64 || '' };
};

export const joinBase64 = (
  icon:
    | {
        format: string | null | undefined;
        base64: string | null | undefined;
      }
    | FileType
    | null
    | undefined
): string => {
  if (!icon) return '';
  const { format, base64 } = icon;
  return format && base64 ? `${format},${base64}` : '';
};

interface IAdditionalValidation {
  maxLength?: number;
  regex?: RegExp;
}

export const validateName = (
  name: string,
  errorOptions?: {
    message?: (validationOption: IAdditionalValidation) => string;
    additionalValidation?: IAdditionalValidation;
    shouldValidate?: {
      isEmpty?: boolean;
      isMaxLength?: boolean;
      isRegex?: boolean;
    };
  }
): {
  isValid: boolean;
  message?: string;
} => {
  const { message, additionalValidation, shouldValidate } = errorOptions || {};
  const {
    maxLength = 100,
    regex = /^[a-zA-Zа-яА-ЯёЁӘәҒғҚқҢңӨөҰұҮүҺһІі0-9\s-]+$/
  } = additionalValidation || {};
  const {
    isEmpty = true,
    isMaxLength = true,
    isRegex = true
  } = shouldValidate || {};

  if (isEmpty && (!name || name.trim().length === 0))
    return {
      isValid: false,
      message: message?.({ maxLength, regex }) || 'Пожалуйста, введите значение'
    };
  if (isMaxLength && name.length > maxLength)
    return {
      isValid: false,
      message:
        message?.({ maxLength, regex }) ||
        `Значение не должно превышать ${maxLength} символов`
    };
  if (isRegex && name && !regex.test(name))
    return {
      isValid: false,
      message:
        message?.({ maxLength, regex }) ||
        'Значение содержит недопустимые символы'
    };
  return { isValid: true };
};
